import { Suspense, type ReactNode, type JSX } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { lazyWithReload } from '@lib/lazyWithReload';
import { AppLayout } from '@app/layout/AppLayout';
import { SignInPage } from '@features/auth/pages/SignInPage';
import { ForgotPasswordPage } from '@features/auth/pages/ForgotPasswordPage';
import { OAuthCallbackPage } from '@features/auth/pages/OAuthCallbackPage';
import { OAuthStartPage } from '@features/auth/pages/OAuthStartPage';
import { LogoutPage } from '@features/auth/pages/LogoutPage';
import { OnboardPage } from '@features/auth/pages/OnboardPage';
import { PermissionGate } from '@components/composition/PermissionGate';
import { FeatureErrorBoundary } from '@components/composition/FeatureErrorBoundary';
import { FlagGate } from '@components/composition/FlagGate';
import { ROUTING_V3_FLAG, ROUTING_V3_FLAG_DEFAULT } from '@features/routing-v3/flag';
import { PluginsRoutes } from '@features/integration/routes';

/**
 * Top-level route table.
 * - Public routes (sign-in, forgot, OAuth) render outside <AppLayout> so the
 * user doesn't see the shell before they're authenticated. They stay
 * EAGERLY imported because they're on the initial-load critical path.
 * - Everything else routes through <AppLayout> which gates on
 * `useIsAuthenticated` and redirects to /signin with a `from` state.
 * - Feature pages are CODE-SPLIT via `React.lazy` — each feature ships as
 * its own chunk so the initial bundle stays small as the app grows.
 * - Each feature route is wrapped in <FeatureErrorBoundary> so a render
 * error inside a single feature shows a scoped fallback instead of
 * replacing the entire shell with the app-root Sentry boundary.
 * Permission gating: each feature route wraps its tree in <PermissionGate> so
 * URL access is blocked even when the sidebar link is hidden.
 */

// Lazy feature chunks. Each lazy import becomes its own Vite chunk in build.
const HomePage = lazyWithReload(() => import('@features/home/HomePage').then((m) => ({ default: m.HomePage })));
const OrdersRoutes = lazyWithReload(() =>
  import('@features/orders/routes').then((m) => ({ default: m.OrdersRoutes })),
);
const MerchantsRoutes = lazyWithReload(() =>
  import('@features/merchant/routes').then((m) => ({ default: m.MerchantsRoutes })),
);
const VendorsRoutes = lazyWithReload(() =>
  import('@features/vendor/routes').then((m) => ({ default: m.VendorsRoutes })),
);
const ShipmentsRoutes = lazyWithReload(() =>
  import('@features/shipments/routes').then((m) => ({ default: m.ShipmentsRoutes })),
);
const RoutingRoutes = lazyWithReload(() =>
  import('@features/routing/routes').then((m) => ({ default: m.RoutingRoutes })),
);
const RoutingV3Routes = lazyWithReload(() =>
  import('@features/routing-v3/routes').then((m) => ({ default: m.RoutingV3Routes })),
);
const UsersRoutes = lazyWithReload(() =>
  import('@features/users/routes').then((m) => ({ default: m.UsersRoutes })),
);
const UserProfilePage = lazyWithReload(() =>
  import('@features/users/UserProfilePage').then((m) => ({ default: m.UserProfilePage })),
);
const WebhooksRoutes = lazyWithReload(() =>
  import('@features/webhooks/routes').then((m) => ({ default: m.WebhooksRoutes })),
);
const SkuMappingsRoutes = lazyWithReload(() =>
  import('@features/skuMappings/routes').then((m) => ({ default: m.SkuMappingsRoutes })),
);
const ProductMappingRoutes = lazyWithReload(() =>
  import('@features/skuMappings/ProductMapping/routes').then((m) => ({ default: m.ProductMappingRoutes })),
);
const AccountInfoRoutes = lazyWithReload(() =>
  import('@features/accountInfo/routes').then((m) => ({ default: m.AccountInfoRoutes })),
);
const ExportsRoutes = lazyWithReload(() =>
  import('@features/exports/routes').then((m) => ({ default: m.ExportsRoutes })),
);
const CatalogsRoutes = lazyWithReload(() =>
  import('@features/catalog/routes').then((m) => ({ default: m.CatalogsRoutes })),
);
const InventoryRoutes = lazyWithReload(() =>
  import('@features/inventory/routes').then((m) => ({ default: m.InventoryRoutes })),
);
const ProductsRoutes = lazyWithReload(() =>
  import('@features/products/routes').then((m) => ({ default: m.ProductsRoutes })),
);
const NotFoundPage = lazyWithReload(() =>
  import('@components/composition/NotFoundPage').then((m) => ({ default: m.NotFoundPage })),
);

/** Minimal centered fallback for in-flight lazy chunks. Replace with a
 * full-shell skeleton if/when one exists. */
function RouteLoadingFallback(): JSX.Element {
  return (
    <div
      className="flex h-full min-h-[40vh] items-center justify-center text-muted-foreground"
      data-testid="route-loading"
    >
      <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
      <span className="sr-only">Loading…</span>
    </div>
  );
}

/** Wraps each lazy feature in (a) Suspense for the chunk fetch + (b) a
 * scoped error boundary. `useLocation().pathname` is the resetKey — a
 * transient render error doesn't survive a navigation. */
function FeatureSlot({ name, children }: { name: string; children: ReactNode }): JSX.Element {
  const { pathname } = useLocation();
  return (
    <FeatureErrorBoundary featureName={name} resetKeys={[pathname]}>
      <Suspense fallback={<RouteLoadingFallback />}>{children}</Suspense>
    </FeatureErrorBoundary>
  );
}

export function AppRoutes(): JSX.Element {
  return (
    <Routes>
      {/* Public auth routes — eager. */}
      <Route path="/signin" element={<SignInPage />} />
      {/* `/signin/oauth` INITIATES the OIDC flow (redirects to Keycloak);
          `/signin/oauth/redirect` is the callback that exchanges the `?code`.
          The portalshadcn migration mistakenly pointed both at the callback. */}
      <Route path="/signin/oauth" element={<OAuthStartPage />} />
      <Route path="/signin/oauth/redirect" element={<OAuthCallbackPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/forgot-password/:token" element={<ForgotPasswordPage />} />
      {/* OAuth logout route (legacy parity) — runs the logout sequence and
          redirects to /signin instead of falling through to the 404 catch-all. */}
      <Route path="/admin/logout" element={<LogoutPage />} />
      {/* Accept-invite (onboard) — public. The vendor path must precede the
          generic one so `/onboard/vendor/:code` matches the vendor branch. */}
      <Route path="/onboard/vendor/:invitationCode" element={<OnboardPage />} />
      <Route path="/onboard/:invitationCode" element={<OnboardPage />} />

      {/* Authenticated shell + protected routes — lazy + error-bounded. */}
      <Route element={<AppLayout />}>
        <Route index element={<Navigate to="/home" replace />} />
        <Route path="/home" element={<FeatureSlot name="home"><HomePage /></FeatureSlot>} />
        {/*
          Own profile — intentionally NOT behind the users:search PermissionGate
          (unlike /admin/users/*), so any authenticated user can view their own
          profile even with no user-management permission. Mirrors legacy `/me`
          → <UserDetails ownProfile />, which sat outside the gated Users area.
        */}
        <Route path="/profile" element={<FeatureSlot name="profile"><UserProfilePage /></FeatureSlot>} />
        <Route
          path="/orders/*"
          element={
            <PermissionGate
              resource="order"
              action="search"
              fallback={<Navigate to="/home" replace />}
            >
              <FeatureSlot name="orders"><OrdersRoutes /></FeatureSlot>
            </PermissionGate>
          }
        />
        {/*
          Shipments — SA + vendor only. The role gate (superAdmin || vendor)
          lives inside ShipmentsRoutes and redirects merchants to /home,
          faithful to legacy where Shipments has no merchant route/nav. A
          route-level PermissionGate(vendor:search) would leak to merchant
          accounts that carry that permission, so the gate is role-based.
        */}
        <Route
          path="/shipments/*"
          element={<FeatureSlot name="shipments"><ShipmentsRoutes /></FeatureSlot>}
        />
        {/*
          AccountInfo entry point — no route-level permission gate. The
          inner AccountInfoView decides which tab(s) to show based on
          `merchant:get` and `vendor:search`, and renders a
          ForbiddenPlaceholder when the user has neither. A route-level
          gate would block vendor users (who only have vendor:search)
          from reaching their own vendor via the
          /admin/account-info/vendor/:id redirect shim.
        */}
        <Route
          path="/admin/account-info/*"
          element={<FeatureSlot name="account-info"><AccountInfoRoutes /></FeatureSlot>}
        />
        {/*
          General Information — legacy redirected /admin/generalInfo to the
          unified Account Info surface when `omUnifiedAccountInfoEnabled` was on.
          The new portal treats unified as always-on, so the nav item resolves
          straight to Account Info instead of falling through to the 404 catch-all.
          Both the new kebab-case path and the legacy camelCase path are
          redirected so old bookmarks/deep-links keep working.
        */}
        <Route
          path="/admin/general-info/*"
          element={<Navigate to="/admin/account-info" replace />}
        />
        <Route
          path="/admin/generalInfo/*"
          element={<Navigate to="/admin/account-info" replace />}
        />
        <Route
          path="/admin/exports/*"
          element={
            <PermissionGate
              resource="insight"
              action="report_get"
              fallback={<Navigate to="/home" replace />}
            >
              <FeatureSlot name="exports"><ExportsRoutes /></FeatureSlot>
            </PermissionGate>
          }
        />
        <Route
          path="/admin/merchants/*"
          element={
            <PermissionGate
              resource="merchant"
              action="get"
              fallback={<Navigate to="/home" replace />}
            >
              <FeatureSlot name="merchants"><MerchantsRoutes /></FeatureSlot>
            </PermissionGate>
          }
        />
        <Route
          path="/admin/vendors/*"
          element={
            <PermissionGate
              resource="vendor"
              action="search"
              fallback={<Navigate to="/home" replace />}
            >
              <FeatureSlot name="vendors"><VendorsRoutes /></FeatureSlot>
            </PermissionGate>
          }
        />
        {/*
          Routing (PROD-915) — one URL, two implementations. The flag picks
          V3 (children) or legacy (fallback); RouteLoadingFallback holds the
          route while LaunchDarkly initializes so a cold load never flashes
          the wrong implementation. /admin/routing-v3 no longer exists
          (deep links fall through to the 404 catch-all).
        */}
        <Route
          path="/admin/routing/*"
          element={
            <PermissionGate
              resource="routing"
              action="strategies_get"
              fallback={<Navigate to="/home" replace />}
            >
              <FlagGate
                flag={ROUTING_V3_FLAG}
                defaultValue={ROUTING_V3_FLAG_DEFAULT}
                fallback={<FeatureSlot name="routing"><RoutingRoutes /></FeatureSlot>}
                pending={<RouteLoadingFallback />}
              >
                <FeatureSlot name="routing-v3"><RoutingV3Routes /></FeatureSlot>
              </FlagGate>
            </PermissionGate>
          }
        />
        <Route
          path="/admin/users/*"
          element={
            <PermissionGate
              resource="users"
              action="search"
              fallback={<Navigate to="/home" replace />}
            >
              <FeatureSlot name="users"><UsersRoutes /></FeatureSlot>
            </PermissionGate>
          }
        />
        <Route
          path="/admin/webhooks/*"
          element={
            <PermissionGate
              resource="notification"
              action="get"
              fallback={<Navigate to="/home" replace />}
            >
              <FeatureSlot name="webhooks"><WebhooksRoutes /></FeatureSlot>
            </PermissionGate>
          }
        />
        {/*
          SKU Mappings — super-admin only. The nav entry restricts to
          superAdmin and there's no resource/action permission for the surface,
          so the role gate lives inside SkuMappingsRoutes (a non-SA deep-link
          redirects to /home), mirroring the Shipments role-guard approach.
        */}
        <Route
          path="/admin/sku-mappings/*"
          element={<FeatureSlot name="sku-mappings"><SkuMappingsRoutes /></FeatureSlot>}
        />
        {/*
          AI Product Mapping — super-admin only + flag-gated
          (omProd722AiCatalogMappingAssistant). Both gates live inside
          ProductMappingRoutes (a non-SA or flag-off deep-link redirects),
          mirroring the SkuMappings role-guard approach.
        */}
        <Route
          path="/admin/product-mapping/*"
          element={<FeatureSlot name="product-mapping"><ProductMappingRoutes /></FeatureSlot>}
        />
        <Route path="/admin/plugins/*" element={<PluginsRoutes Slot={FeatureSlot} />} />
        <Route
          path="/admin/catalogs/*"
          element={
            <PermissionGate
              resource="product"
              action="merchant_variants_view"
              fallback={<Navigate to="/home" replace />}
            >
              <FeatureSlot name="catalogs"><CatalogsRoutes /></FeatureSlot>
            </PermissionGate>
          }
        />
        <Route
          path="/admin/inventory/*"
          element={
            <PermissionGate
              anyOf={[
                { resource: 'vendor', action: 'search' },
                { resource: 'vendor', action: 'create' },
              ]}
              fallback={<Navigate to="/home" replace />}
            >
              <FeatureSlot name="inventory"><InventoryRoutes /></FeatureSlot>
            </PermissionGate>
          }
        />
        {/*
          Products — SA + vendor only, gated `(superAdmin||vendor) &&
          omVendorPortalEnabled`. Like Shipments/SkuMappings the role+flag gate
          lives INSIDE ProductsRoutes (a merchant / flag-off deep-link redirects
          to /home); a route-level vendor:* PermissionGate would leak to
          merchants carrying that permission. Vendor scoping is handled by the
          EntitySelector wrapper inside ProductsRoutes.
        */}
        <Route
          path="/admin/products/*"
          element={<FeatureSlot name="products"><ProductsRoutes /></FeatureSlot>}
        />
        {/* Catch-all: any path matching no route above is a genuine 404. Renders
            inside the shell so the user keeps the sidebar/topbar and can navigate
            away. SWA rewrites every unmatched path to index.html, so React Router
            owns the 404 — there is no server-side 404 status. */}
        <Route
          path="*"
          element={<FeatureSlot name="not-found"><NotFoundPage /></FeatureSlot>}
        />
      </Route>
    </Routes>
  );
}
